import {useForm} from "@tanstack/react-form";
import {createFileRoute} from "@tanstack/react-router"
import {StandardUnitCodes, SystemConfig} from "../../../rpc/webrpc-client.gen.ts";
import {useCallback, useEffect} from "react";
import {useSetSystemConfigMutation} from "../../../tq/tqx.tsx";
import {useSystemConfig} from "../../../rpc/sysconf.tsx";
import {useBomb} from "../../../bomb/bomb.tsx";
import {Box, Button, FormControl, FormLabel, Input as JoyInput, Stack, Switch, useTheme} from "@mui/joy";
import {HCBox} from "../../../components/HCBox.tsx";
import {useTranslation} from "react-i18next";
import {z} from "zod/v4";
import {SuspenseDialog} from "../../../components/SuspenseDialog.tsx";
import {PageBody, PageHeader, PageShell} from "../../../routing/shell.tsx";
import {TopBar} from "../../../components/TopBar.tsx";
import {NumericFormat} from "react-number-format";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowsSpin} from "@fortawesome/pro-regular-svg-icons";

const defaultExportPeriod = 60;

const SafeSystemConfig = z.preprocess((raw: SystemConfig) => {
    return {
        vendor: raw.vendor,
        dratBanner: raw.dratBanner,
        standardUnitCodes: raw.standardUnitCodes,
        driversCanAddWastes: !!raw.driversCanAddWastes,
        fieldConfirmSurcharges: !!raw.fieldConfirmSurcharges,
        autoExportIntervalMinutes: raw.autoExportIntervalMinutes ?? defaultExportPeriod,
    } as const;
}, z.object({
    vendor: SystemConfig.shape.vendor,
    dratBanner: z.string(),
    standardUnitCodes: StandardUnitCodes,
    driversCanAddWastes: z.boolean(),
    fieldConfirmSurcharges: z.boolean(),
    autoExportIntervalMinutes: z.number(),
}));
type SafeSystemConfig = z.infer<typeof SafeSystemConfig>;

const booleanFieldNames = ["driversCanAddWastes", "fieldConfirmSurcharges"] as const;

function SysconfEditor({currentConfig}: { currentConfig: SystemConfig }) {
    const xcfg = SafeSystemConfig.parse(currentConfig);
    const bomb = useBomb();
    const {t} = useTranslation();
    const theme = useTheme();
    const {mutateAsync: performSubmit} = useSetSystemConfigMutation();
    const handleSubmit = useCallback(({value}: { value: SystemConfig }) => {
        return performSubmit(value);
    }, [performSubmit]);

    const form = useForm({
        defaultValues: xcfg,
        validators: {
            onSubmitAsync: handleSubmit,
        }
    });

    const tqrv = useSystemConfig();
    useEffect(() => {
        if (tqrv.data) {
            const zprv = SafeSystemConfig.safeParse(tqrv.data);
            if (zprv.success) {
                form.reset(zprv.data);
            }
        }
    }, [form, tqrv.data]);

    return <form
        onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit().catch(bomb);
        }}>
        <Stack direction="column" spacing="1.5rem">
            <HCBox>
                <form.Field
                    name="vendor"
                    children={(field) =>
                        <Stack direction="row" sx={{alignItems: "center"}}>
                            <Box>{t("sysconf.vendor")}</Box>
                            <Box>{field.state.value}</Box>
                        </Stack>}/>
            </HCBox>
            <HCBox>
                <Stack direction="row" sx={{alignItems: "center"}}>
                    {booleanFieldNames.map(fn => {
                        return <Box key={fn} sx={{px: 2}}>
                            <form.Field
                                name={fn}
                                children={(field) => {
                                    return <FormControl orientation="horizontal" sx={{
                                        flex: 1,
                                        borderWidth: "2px",
                                        borderStyle: "solid",
                                        borderColor: theme.palette.primary[900],
                                        p: 0.8
                                    }}
                                                        color={field.state.value ? "primary" : "neutral"}>
                                        <FormLabel>{t(`sysconf.${fn}`)}</FormLabel>
                                        <Switch checked={field.state.value} onChange={(evt) => {
                                            const nv = !!evt.target.value;
                                            field.handleChange(nv);
                                        }}/>
                                    </FormControl>
                                }}/>
                        </Box>
                    })}
                </Stack>
            </HCBox>
            <HCBox>
                <form.Field name="autoExportIntervalMinutes"
                            children={(field) => <Stack direction="row" sx={{alignItems: "center"}}>
                                <FormControl orientation="horizontal">
                                    <FormLabel>{t("sysconf.autoExportIntervalMinutes")}</FormLabel>
                                    <NumericFormat
                                        value={field.state.value}
                                        onValueChange={(values) => {
                                            field.handleChange(Math.round((values.floatValue ?? 0)));
                                        }}
                                        customInput={JoyInput}
                                        slotProps={{input: {type: "number", style: {textAlign: "right"}}}}
                                        sx={{
                                            maxWidth: "8rem",
                                            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                                display: "none"
                                            },
                                            "& input[type=number]": {
                                                MozAppearance: "textfield"
                                            }
                                        }}
                                        valueIsNumericString={true}
                                    />
                                    <Box sx={{ml: 1}}>{t("duration.min")}</Box>
                                </FormControl>
                            </Stack>}/>
            </HCBox>

            <HCBox>
                <form.Subscribe
                    selector={(state) => [state.canSubmit, state.isSubmitting]}
                    children={([canSubmit, isSubmitting]) => (
                        <Button type="submit" disabled={!canSubmit}
                                sx={{
                                    py: 1, px: 8, mt: 1.8, mb: 3.2,
                                    fontSize: theme.fontSize.xl
                                }}>
                            {isSubmitting
                                ? <FontAwesomeIcon icon={faArrowsSpin} size="2x" spin={true}
                                                   style={{color: "white", mixBlendMode: "difference"}}/>
                                : t("sysconf.submit")}
                        </Button>
                    )}
                />
            </HCBox>
        </Stack>
    </form>
}

function SysconfPage() {
    const curConfTqrv = useSystemConfig();

    if (curConfTqrv.isPending) {
        return <SuspenseDialog message="loading system configuration"/>
    }

    return <PageShell>
        <PageHeader><TopBar/></PageHeader>
        <PageBody>
            <SysconfEditor currentConfig={curConfTqrv.data}/>
        </PageBody>
    </PageShell>;
}

export const Route = createFileRoute("/_auth/admin/sysconf")({
    component: SysconfPage,
})

